import type {
  SalesAddToCrmContact,
  SalesEmailConfidence,
  SalesOrgLeadDetail,
} from "@xop/dashboard-shared";

/**
 * Pure contact extraction for the lead page's Contact card.
 *
 * The agent writes a multi-sentence `suggested_contact_path` prose blob
 * ("Primary: the contact form … Named contacts: a LinkedIn search … Press:
 * media@… NRC correspondence path: …"). Operators don't want to read that —
 * they want the *core info*: the website, the emails, and any named people.
 * So we pull the structured bits OUT of the prose (and the named `persons`)
 * and throw the narrative away. Framework-free so it's unit-testable.
 */

export interface ContactEmail {
  email: string;
  /** Confidence when the email came from a named person; null when scraped
   * out of the agent's prose (we don't know how it was validated). */
  confidence: SalesEmailConfidence | null;
}

export interface ContactPerson {
  id: string;
  name: string;
  title: string;
  email: string | null;
  /** A direct phone line if the page listed one beside this buyer. */
  phone: string | null;
  linkedin: string | null;
  source: string | null;
}

export interface LeadContactInfo {
  website: string | null;
  emails: ContactEmail[];
  /** Org-level phone number(s) — the main / switchboard line (the lead's
   * grounded `phone`) plus any explicit `tel:` links lifted from the prose.
   * The reach-by-phone floor when there's no email. */
  phones: string[];
  people: ContactPerson[];
  /** True when there's anything worth rendering. */
  hasAny: boolean;
}

// Conservative email matcher — good enough to lift addresses out of prose.
const EMAIL_RE = /[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}/g;
// Explicit `tel:` links in the agent's prose — unambiguous phone numbers.
const TEL_RE = /tel:\s*(\+?\d[\d().\-\s]{5,}\d)/gi;

/** Significant digits of a phone string, used as the dedup key. Drops a
 * leading international `00`/`+` trunk prefix so the same number written two
 * ways collapses. Mirrors the agent's `_phone_digits`. */
function phoneDigits(s: string): string {
  const d = (s ?? "").replace(/\D/g, "");
  return d.startsWith("00") ? d.slice(2) : d;
}

/** Normalized name key — case- and whitespace-insensitive. Matches the
 * agent's `persons.upsert_person` + the DB unique index, so the UI collapses
 * the same contact even if the API ever returns legacy duplicate rows. */
function nameKey(name: string): string {
  return name.replace(/\s+/g, " ").trim().toLowerCase();
}

export function contactInfo(lead: SalesOrgLeadDetail): LeadContactInfo {
  const persons = lead.persons ?? [];
  // Dedup people by normalized name (the agent used to write the SAME buyer
  // N times — e.g. AES had "Katie Lau" ×6). Keep first-seen order; merge
  // missing email/linkedin/source and prefer the longer (more specific) title.
  const byKey = new Map<string, ContactPerson>();
  const order: string[] = [];
  persons.forEach((p, i) => {
    const key = nameKey(p.full_name) || `__unnamed_${i}`;
    const existing = byKey.get(key);
    if (!existing) {
      byKey.set(key, {
        id: p.id,
        name: p.full_name,
        title: p.title,
        email: p.email ?? null,
        phone: p.phone ?? null,
        linkedin: p.linkedin_url ?? null,
        source: p.source_url ?? null,
      });
      order.push(key);
      return;
    }
    if (!existing.email && p.email) existing.email = p.email;
    if (!existing.phone && p.phone) existing.phone = p.phone;
    if (!existing.linkedin && p.linkedin_url) existing.linkedin = p.linkedin_url;
    if (!existing.source && p.source_url) existing.source = p.source_url;
    if (p.title && p.title.length > existing.title.length) existing.title = p.title;
  });
  const people: ContactPerson[] = order.map((k) => byKey.get(k)!);

  const seen = new Set<string>();
  const emails: ContactEmail[] = [];
  // Named-person emails first — they carry a real confidence signal.
  for (const p of persons) {
    if (!p.email) continue;
    const key = p.email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    emails.push({ email: p.email, confidence: p.email_confidence ?? null });
  }
  // Then any address embedded in the agent's prose (contact path / brief /
  // summary) — we keep the address, discard the surrounding narrative.
  const brief = lead.briefs?.[0];
  const prose = [
    brief?.suggested_contact_path,
    brief?.why_now_markdown,
    lead.agent_summary,
  ]
    .filter((s): s is string => Boolean(s))
    .join("\n");
  for (const match of prose.matchAll(EMAIL_RE)) {
    const email = match[0];
    const key = email.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    emails.push({ email, confidence: null });
  }

  // Org phone(s): the grounded main line on the lead first (the reach-by-phone
  // floor), then any explicit `tel:` link the agent left in its prose. Deduped
  // by significant digits so "+1 (662) 915-7211" and "662-915-7211" collapse.
  const phones: string[] = [];
  const seenPhone = new Set<string>();
  const addPhone = (raw: string | null | undefined): void => {
    const cleaned = (raw ?? "").replace(/\s+/g, " ").trim();
    if (!cleaned) return;
    const key = phoneDigits(cleaned);
    if (key.length < 7 || seenPhone.has(key)) return;
    seenPhone.add(key);
    phones.push(cleaned);
  };
  addPhone(lead.phone);
  for (const match of prose.matchAll(TEL_RE)) addPhone(match[1]);

  const website = lead.domain ?? null;
  return {
    website,
    emails,
    phones,
    people,
    hasAny:
      Boolean(website) ||
      emails.length > 0 ||
      phones.length > 0 ||
      people.length > 0,
  };
}

/** Split a free-text full name into the CRM's first/last columns. The agent
 * stores one `full_name` string; the CRM keeps them apart. First token →
 * first_name, the rest → last_name. */
function splitName(full: string): { first?: string; last?: string } {
  const parts = (full ?? "").trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return {};
  const first = parts[0]!;
  const last = parts.slice(1).join(" ");
  return last ? { first, last } : { first };
}

/**
 * Build the FULL set of CRM contacts to create when converting a lead to a
 * prospect. The previous bug only carried `people[0]` — a multi-buyer lead
 * landed in the CRM with a single contact and the rest were silently dropped.
 *
 * Here EVERY named buyer travels (deduped by `contactInfo`), and then every
 * remaining email — including a generic org inbox like `info@`/`sales@` that
 * isn't tied to a named person — is added as an email-only contact. A generic
 * inbox is still a real way to reach the prospect, so it must be saved as a
 * contact person rather than discarded (operator requirement).
 *
 * Deduped by lower-cased email so a buyer's address isn't also added as a
 * standalone email-only contact. Contacts with no usable field are skipped.
 */
export function toCrmContacts(info: LeadContactInfo): SalesAddToCrmContact[] {
  const out: SalesAddToCrmContact[] = [];
  const seenEmail = new Set<string>();

  // Email → confidence, so each named buyer's address carries its trust signal
  // into the CRM (the recipient picker flags guessed vs verified). Built from
  // `info.emails`, which already pairs every address with its confidence.
  const confByEmail = new Map<string, SalesEmailConfidence>();
  for (const e of info.emails) {
    if (e.confidence) confByEmail.set(e.email.toLowerCase(), e.confidence);
  }

  for (const p of info.people) {
    const { first, last } = splitName(p.name);
    const conf = p.email ? confByEmail.get(p.email.toLowerCase()) : undefined;
    const c: SalesAddToCrmContact = {
      ...(first ? { first_name: first } : {}),
      ...(last ? { last_name: last } : {}),
      ...(p.email ? { email: p.email } : {}),
      ...(p.title ? { job_title: p.title } : {}),
      ...(p.phone ? { phone: p.phone } : {}),
      ...(conf ? { email_confidence: conf } : {}),
    };
    if (p.email) seenEmail.add(p.email.toLowerCase());
    // A name OR an email OR a phone makes this a contact worth creating.
    if (c.first_name || c.last_name || c.email || c.phone) out.push(c);
  }

  // Generic / prose-scraped emails not already tied to a named buyer — the
  // info@ / sales@ / media@ inboxes. Save each as an email-only contact,
  // carrying its confidence when the agent knew one (generic inboxes scraped
  // from prose are usually unknown ⇒ omitted).
  for (const e of info.emails) {
    const key = e.email.toLowerCase();
    if (seenEmail.has(key)) continue;
    seenEmail.add(key);
    out.push({
      email: e.email,
      ...(e.confidence ? { email_confidence: e.confidence } : {}),
    });
  }

  return out;
}
